import { Component } from '@theme/component';

export class AnnouncementBar extends Component {
  #current = 0;
  #interval;
  #listeners;
  #motion;
  #userPaused = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.addEventListener('mouseenter', this.suspend, options);
    this.addEventListener('mouseleave', this.resume, options);
    this.addEventListener('focusin', this.suspend, options);
    this.addEventListener('focusout', this.resume, options);
    document.addEventListener('visibilitychange', this.resume, options);
    this.#motion.addEventListener('change', this.resume, options);
    this.current = this.#current;
    this.resume();
  }

  disconnectedCallback() {
    this.suspend();
    this.#listeners?.abort();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.current = this.#current;
    this.resume();
  }

  next() { this.pause(); this.current += 1; }
  previous() { this.pause(); this.current -= 1; }
  togglePlayback() { this.#userPaused = !this.#userPaused; this.resume(); }
  pause() { this.#userPaused = true; this.resume(); }
  play() { this.#userPaused = false; this.resume(); }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    this.suspend();
    const reduced = this.#motion?.matches;
    const paused = this.#userPaused || reduced;
    this.toggleAttribute('paused', Boolean(paused));
    const button = this.querySelector('[data-playback]');
    if (button) {
      button.hidden = !this.autoplayInterval;
      button.disabled = Boolean(reduced);
      button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    }
    if (paused || !this.autoplayInterval || document.hidden || !this.isConnected) return;
    this.#interval = setInterval(() => {
      if (this.matches(':hover, :focus-within') || document.hidden) return;
      this.current += 1;
    }, this.autoplayInterval);
  };

  get autoplayInterval() {
    const value = Number(this.getAttribute('autoplay'));
    return Number.isFinite(value) && value > 0 ? Math.max(5000, value * 1000) : 0;
  }
  get current() { return this.#current; }
  set current(value) {
    const slides = this.refs.slides || [];
    if (!slides.length) return;
    this.#current = (value % slides.length + slides.length) % slides.length;
    slides.forEach((slide, index) => {
      const active = index === this.#current;
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
  }
}
if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
